import DateRangePicker from '@wojtekmaj/react-daterange-picker'
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useContext, useEffect, useState } from 'react'

dayjs.extend(utc)
dayjs.extend(timezone)

// Helper function to get user's timezone
function guessTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return 'UTC'
  }
}
import 'react-calendar/dist/Calendar.css'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import ClinicHeader from 'src/clinic/components/ClinicHeader'
import {
  EDVCDateContainer,
  EDVCParent,
  FloatingHeaderContainerDayCal,
} from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingsPageStyles'
import {
  addServiceProviderToData,
  selectServiceProvidersData,
} from 'src/clinic/reducers/serviceProviders/serviceProvidersReducer'
import { getCPDataWithoutParams } from 'src/clinic/services'
import { GenericHeaderBackButton } from 'src/consumer/components/ActivityPlansPage/MyPlanComponents'
import { getTimezoneOffset } from 'src/consumer/helpers/utilFunctions'
import { appVersion, platformWeb } from 'src/consumer/services'
import { ClinicDataContext } from 'src/context/ClinicDataContext'
import { PageMainContentContainer } from 'src/mentor/components/MentorAvailabilityPage/MentorAvailabilityPageStyles'
import { HeaderGreeting } from 'src/mentor/components/MentorHeader/MentorHeaderStyles'
import ScheduledPatientsTable from 'src/supportPortal/views/scheduledPatients/components/ScheduledPatientsTable'

export default function ClinicAssistantPlannedAppointmentsTab() {
  const { ownerData, assistantData } = useContext(ClinicDataContext)
  const { clinicId } = useParams()

  // Date range state management
  const [selectedDate, setSelectedDate] = useState(dayjs().tz(guessTimezone()))
  const [selectedEndDate, setSelectedEndDate] = useState(
    dayjs().add(10, 'days').tz(guessTimezone()).endOf('day'),
  )
  const assistantProfileForThisClinic = assistantData?.clinicAssistantProfileData?.find(
    (item) => item?.clinicId === clinicId,
  )
  const serviceProviders = useSelector(selectServiceProvidersData)
  const dispatch = useDispatch()
  const [loadingObject, setLoadingObject] = useState({
    managersLoading: false,
    mentorProfileDataLoading: false,
  })

  async function getAndSetManagersProfileData() {
    console.log('getAndSetManagersProfileData')
    setLoadingObject((prev) => ({ ...prev, managersLoading: true }))

    if (Array.isArray(assistantProfileForThisClinic?.supportRoles)) {
      setLoadingObject((prev) => ({ ...prev, mentorProfileDataLoading: true }))
      await Promise.all(
        assistantProfileForThisClinic?.supportRoles?.map(async (supportRole) => {
          // check if service provider data is available in redux
          const foundSvcProviderInRedux = serviceProviders?.find(
            (item) => item?.id === supportRole?.managerId,
          )
          if (Boolean(foundSvcProviderInRedux)) {
          } else {
            // get from API and set in local state and redux state
            const response = await getCPDataWithoutParams(
              `/api/secure/clinic-assistant/service-provider?id=${
                supportRole?.managerId
              }&slug=&zoneOffset=${encodeURIComponent(
                getTimezoneOffset(),
              )}&appVersion=${appVersion}&platformType=${platformWeb}`,
            )
            if (response.status === 200) {
              dispatch(addServiceProviderToData(response?.data))
            }
          }
        }),
      )
      setLoadingObject((prev) => ({ ...prev, mentorProfileDataLoading: false }))
    }
    setLoadingObject((prev) => ({ ...prev, managersLoading: false }))
  }

  const associatedClinicBrandsData = assistantData?.associatedClinicsData
  const thisClinicData = associatedClinicBrandsData?.find((item) => item?.id === clinicId)

  // Calculate UTC formatted date strings for API calls
  const selectedDateUtcStartTime = dayjs(selectedDate)
    .utc()
    .startOf('day')
    .format('YYYY-MM-DDTHH:mm:ss[Z]')
  const selectedDateUtcEndTime = dayjs(selectedEndDate)
    .utc()
    .endOf('day')
    .format('YYYY-MM-DDTHH:mm:ss[Z]')

  useEffect(() => {
    getAndSetManagersProfileData()
  }, [])

  return (
    <>
      <ClinicHeader
        component={
          <div className="d-flex gap-2 justify-content-center align-items-center">
            <GenericHeaderBackButton
              clickfunction={() => window.history.back()}
              styles={{ margin: '20px' }}
            />
            <HeaderGreeting main>Upcoming Appointments</HeaderGreeting>
          </div>
        }
      />
      <PageMainContentContainer style={{ maxWidth: '1200px', marginTop: '16px' }}>
        <EDVCParent>
          <div className="d-flex flex-column align-items-center gap-1 w-100">
            <FloatingHeaderContainerDayCal style={{ zIndex: 1 }} className="w-100">
              <div
                className="w-100 d-flex flex-column"
                style={{ position: 'relative', padding: '8px' }}
              >
                <div className="d-flex align-items-center justify-content-between w-100 mb-2">
                  <EDVCDateContainer style={{ padding: '8px 0' }}>
                    <div className="d-flex w-100 justify-content-center">
                      <DateRangePicker
                        clearIcon={null}
                        onChange={(val) => {
                          if (val?.[0] && val?.[1]) {
                            setSelectedDate(dayjs(val[0]))
                            setSelectedEndDate(dayjs(val[1]))
                          }
                        }}
                        value={[selectedDate.toDate(), selectedEndDate.toDate()]}
                      />
                    </div>
                  </EDVCDateContainer>
                </div>
              </div>
            </FloatingHeaderContainerDayCal>
            <ScheduledPatientsTable
              clinicBrandParam={'clinicBrandId'}
              clinicBrandIds={[thisClinicData?.clinicBrandId]}
              clinicLocationParam={'clinicId'}
              clinicIds={[thisClinicData?.id]}
              isEventTypesNotMandatory
              patientVisitInfoLabel='Doctor/Therapist'
              reportFetchingApiURL="/api/secure/clinic-assistant/scheduled-patients"
              fromInDayJsFormat={dayjs(selectedDateUtcStartTime)}
              toInDayJsFormat={dayjs(selectedDateUtcEndTime)}
              listingItemTransformer={(patientData) => {
                // const brand = allClinicBrands?.find((cbr) => cbr?.id === patientData?.clinicBrandId)
                // const clinicLocation = allClinicLocations?.find(
                //   (loc) => loc?.id === patientData?.clinicId,
                // )
                const mentor = serviceProviders?.find((item) => item?.id === patientData?.mentorId)
                // console.log({ mentor, patientData })
                // console.log({ patientData, mentor })
                return {
                  ...patientData,
                  // brandData: brand,
                  // clinicData: clinicLocation,
                  mentorDetails: mentor,
                }
              }}
            />
          </div>
        </EDVCParent>
      </PageMainContentContainer>
    </>
  )
}
